import {
  Component,
  h,
  Element,
  State,
  Watch,
  Listen,
  Prop,
} from "@stencil/core"
import {
  dialogReducer,
  getInitialState,
  DialogState,
  DialogActions,
} from "../dialog-store"
import {
  StoreConnection,
  StateHandle,
  disableBodyScroll,
  enableBodyScroll,
  setCssVariableWindowInnerHeight,
  getAppearance,
  translate,
} from "../../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-dialog-content",
  styleUrl: "dialog-content.scss",
  shadow: true,
})
export class DialogContent {
  private store: StoreConnection<this, DialogState, DialogActions>
  private closeButtonEl?: HTMLSdxButtonElement
  private contentEl?: HTMLDivElement
  private fixedHeightAfterFirstOpen = 0

  @Element() public el!: HTMLSdxDialogContentElement

  @State() public state!: StateHandle<DialogState>

  /**
   * If sdx-select opens a dialog the height should be fixed. A filterable
   * select changes dialog height if user filters.
   * @private
   */
  @Prop() public fixedHeight: boolean = false

  @Watch("state")
  public stateChanged({}, prev: StateHandle<DialogState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }
  }

  @Listen("resize", { target: "window" })
  public onWindowResize() {
    const { display } = this.state.get()

    this.fixedHeightAfterFirstOpen = 0

    if (display === "open") {
      setCssVariableWindowInnerHeight()
    }
  }

  constructor() {
    this.store = new StoreConnection(this, dialogReducer, getInitialState(), [
      "label",
      "type",
      "iconName",
      "iconColorClass",
      "display",
      "toggle",
      "notificationType",
      "alignHeaderCenter",
    ])
  }

  public connectedCallback() {
    // Register self
    this.store.set("contentEl", this.el)
  }

  public disconnectedCallback() {
    // Unregister self
    this.store.set("contentEl", undefined)
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private displayChanged(state: DialogState) {
    switch (state.display) {
      case "opening":
        setCssVariableWindowInnerHeight()
        disableBodyScroll()
        break

      case "open":
        const {
          fixedHeight,
          fixedHeightAfterFirstOpen,
          contentEl,
          el,
          closeButtonEl,
        } = this

        if (fixedHeight && contentEl && !fixedHeightAfterFirstOpen) {
          const contentElHeight = contentEl.getBoundingClientRect().height

          contentEl.style.height = `${contentElHeight}px`
          this.fixedHeightAfterFirstOpen = contentElHeight
        }

        // do not steal focus from child elements (e.g. select filter input field)
        if (!el.contains(document.activeElement)) {
          closeButtonEl?.doFocus()
        }

        break

      case "closed":
        enableBodyScroll()
        break
    }
  }

  private getComponentClassNames() {
    return {
      component: true,
      [getAppearance(this.el)]: true,
      "fixed-height": this.fixedHeight,
    }
  }

  private getHeaderClassNames() {
    const { notificationType, alignHeaderCenter, iconName } = this.state.get()

    return {
      header: true,
      "align-header-center": alignHeaderCenter,
      "has-icon": !!iconName,
      [`${notificationType}`]: !!notificationType,
    }
  }

  private toggle() {
    this.store.set("isUserInteractionInProgress", true)
    this.state.get().toggle()
    this.store.set("isUserInteractionInProgress", false)
  }

  private createClosebutton() {
    return (
      <sdx-button
        class="close-button"
        theme="transparent"
        iconName="icon-close"
        iconSize={2}
        srHint={translate("Close dialog")}
        onClick={() => this.toggle()}
        ref={(el) => (this.closeButtonEl = el)}
      />
    )
  }

  public render() {
    const {
      label,
      type: typeState,
      iconName,
      iconColorClass,
      alignHeaderCenter,
    } = this.state.get()

    return (
      <div
        ref={(el) => (this.contentEl = el)}
        class={this.getComponentClassNames()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sdx-dialog-content-label"
        aria-describedby="sdx-dialog-content-description"
      >
        <sdx-trap-focus
          firstFocusableElement={this.closeButtonEl}
          lang={this.el.lang}
        >
          <div class="content-wrapper">
            {typeState === "closable-modal" && this.createClosebutton()}

            {
              <div class={this.getHeaderClassNames()}>
                {iconName && (
                  <sdx-icon
                    class="icon"
                    iconName={iconName}
                    colorClass={iconColorClass}
                    size={alignHeaderCenter ? 6 : undefined}
                  />
                )}

                <h1 class="h3" id="sdx-dialog-content-label">
                  {label}
                </h1>
              </div>
            }

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div class="slot" id="sdx-dialog-content-description" tabindex={0}>
              <slot />
            </div>
          </div>
        </sdx-trap-focus>
      </div>
    )
  }
}
